import { format, roundToNearestMinutes } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import {
  type FunctionComponent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { StringParam, useQueryParams, withDefault } from 'use-query-params'
import { Skeleton } from 'tailwind-ui'
import { useParams } from 'react-router'
import usePrevious from '@/utils/usePrevious'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import ThreadEmailBox from '@/components/inbox/messaging/email'
import ThreadMessageBox from '@/components/inbox/messaging/message'
import ThreadNoteBox from '@/components/inbox/messaging/note'
import useGlobal from '@/hooks/useGlobal'
import { type InboxThreadEventType } from '@/state/entities/inbox/inbox.types'
import { useAppDispatch } from '@/state/helpers/useApp'
import { usePusherSubscription } from '@/utils/pusher'
import inboxApi from '@/state/inbox/inbox.slice'
import { initServicePagination } from '@/connect-types/backend/service'
import { getFirst } from '@/utils/common'
import ThreadSmsBox from '../messaging/sms'
import ThreadSystemNoteBox from '../messaging/system-note'
import ThreadIncomingFormSubmissionBox from '../messaging/incoming-form-submission'
import ThreadContactReplyBox from '../messaging/contact-reply'

/**
 *
 * @todo - put these in another DIR
 */

const ThreadItemBox: FunctionComponent<{
  threadId: string
}> = ({ threadId }) => {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()

  const [query] = useQueryParams(
    {
      selected_thread: withDefault(StringParam, ''),
    },
    {
      updateType: 'pushIn',
    }
  )

  const [cursor, setCursor] = useState('')
  const [minLoadingComplete, setMinLoadingComplete] = useState(false)

  const { data: thread } = inboxApi.useGetInboxThreadQuery({
    orgId,
    threadId,
  })

  const {
    data: events = initServicePagination<InboxThreadEventType>(),
    isLoading: isEventsLoading,
  } = inboxApi.useGetInboxThreadEventsQuery({
    orgId,
    threadId,
    cursor:
      cursor && cursor.includes(threadId) ? getFirst(cursor.split('_')) : '',
  })

  useEffect(() => {
    setMinLoadingComplete(false)
    const timer = setTimeout(() => {
      setMinLoadingComplete(true)
    }, 1500)
    return () => {
      clearTimeout(timer)
    }
  }, [threadId])

  const [markInboxThreadAsRead] = inboxApi.useMarkInboxThreadAsReadMutation()
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (!threadId) return

    const timer = setTimeout(async () => {
      await markInboxThreadAsRead({ orgId, threadId, read: true }).unwrap()
      dispatch(inboxApi.util.invalidateTags(['counts']))
    }, 10000)

    return () => {
      clearTimeout(timer)
    }
  }, [orgId, threadId, markInboxThreadAsRead])

  const [ref, deferInView] = useInView()
  const inView = useDeferredValue(deferInView)
  const messageContentRef = useRef<HTMLDivElement | null>(null)
  const [showTopDiv, setShowTopDiv] = useState(false)
  const prevCursor = usePrevious(events?.meta.next_cursor)
  const prevLength = usePrevious(events?.data.length)

  useEffect(() => {
    if (!events.meta.next_cursor) return
    const timeout = setTimeout(() => {
      setShowTopDiv(true)
    }, 500)
    return () => {
      clearTimeout(timeout)
    }
  }, [events.meta.next_cursor])

  useEffect(() => {
    if (
      isEventsLoading ||
      !messageContentRef.current ||
      events.data.length === 0
    ) {
      return
    }

    const shouldScrollToBottom =
      !prevLength || // initial load
      (prevLength && events.data.length > prevLength) // new messages

    if (shouldScrollToBottom) {
      const timer = setTimeout(() => {
        messageContentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100) // Reduced timeout for better responsiveness

      return () => {
        clearTimeout(timer)
      }
    }
  }, [events.data.length, isEventsLoading, prevLength])

  useEffect(() => {
    if (inView && Boolean(events.meta.next_cursor)) {
      setCursor(`${events.meta.next_cursor}_${threadId}`)
    }
  }, [inView, events.meta.next_cursor, threadId])

  const getPreviousDate = useCallback(
    (index: number) => {
      if (events.data.length === 0) return ''
      if (!events.data[index]) return ''
      const currentItem = format(
        events.data[index].created_at,
        TimeFormats.StandardDateMonthYear
      )
      if (index === 0) {
        return currentItem
      }
      if (
        format(
          events.data[index - 1]?.created_at,
          TimeFormats.StandardDateMonthYear
        ) !== currentItem
      ) {
        return currentItem
      }
    },
    [events]
  )

  /*
  const isEmailSameAsPrev = useCallback(
    (index: number) => {
      if (events.data.length === 0) return ''

      const currentItem = events.data[index]
      if (!currentItem) return ''
      const currentType = currentItem.eventable_type
      const prevItem = events.data[index + 1]
      if (!prevItem) return false
      if (currentType === 'Message' || currentType === 'Note') {
        if (
          'user_email' in currentItem.eventable &&
          'user_email' in prevItem.eventable
        ) {
          return (
            currentItem.eventable.user_email === prevItem.eventable.user_email
          )
        }
      }
    },
    [events]
  )
    */

  const loadingArray = useMemo(() => {
    const shouldShowLoading =
      !thread?.id ||
      (isEventsLoading && events.data.length === 0) ||
      events.threadId !== thread?.id ||
      !minLoadingComplete

    const length = shouldShowLoading ? 8 : 0

    return Array(length).fill(null)
  }, [
    thread?.id,
    isEventsLoading,
    events.data.length,
    events.threadId,
    query.selected_thread,
    minLoadingComplete,
  ])

  usePusherSubscription<{ id: string; thread_id: string }>(
    `inbox.thread.${threadId}.thread_event.stored`,
    ({ thread_id }) => {
      dispatch(
        inboxApi.util.invalidateTags([{ type: 'thread_events', id: thread_id }])
      )
    }
  )

  usePusherSubscription<{ id: string; thread_id: string }>(
    `inbox.thread.${threadId}.thread_event.updated`,
    ({ thread_id }) => {
      dispatch(
        inboxApi.util.invalidateTags([{ type: 'thread_events', id: thread_id }])
      )
    }
  )

  const showMessages = minLoadingComplete && thread?.id === events?.threadId

  return (
    <div className="flex-1 px-2">
      {showMessages
        ? events.data.map((event, index) =>
            event ? (
              <motion.div
                layout
                key={event.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col [&>[data-date-divider]+*]:border-t-0"
                transition={{
                  opacity: { duration: 0.2 },
                  layout: {
                    type: 'spring',
                    bounce: 0.25,
                    duration: 0.4,
                  },
                }}
              >
                {index === 1 &&
                Boolean(events.meta.next_cursor) &&
                showTopDiv ? (
                  <div ref={ref} />
                ) : null}
                {Boolean(getPreviousDate(index)) && (
                  <div
                    data-date-divider
                    className="new-ui-theme flex items-center gap-3 py-4 text-[var(--foreground)]"
                  >
                    <span className="h-px flex-1 bg-[var(--border)]" />
                    <Skeleton
                      isLoading={
                        isEventsLoading ? events.data.length >= 15 : null
                      }
                    >
                      <span className="rounded-full bg-black/5 px-3 py-1 text-xs font-medium whitespace-nowrap text-[var(--muted-foreground)] dark:bg-white/10">
                        {getPreviousDate(index)}
                      </span>
                    </Skeleton>
                    <span className="h-px flex-1 bg-[var(--border)]" />
                  </div>
                )}

                {event.eventable_type === 'Note' && (
                  <ThreadNoteBox event={event.eventable} />
                )}

                {event.eventable_type === 'Message' && (
                  <ThreadMessageBox
                    threadId={threadId}
                    eventId={event.id}
                    event={event.eventable}
                  />
                )}
                {event.eventable_type === 'Incoming SMS Message' && (
                  <ThreadSmsBox event={event.eventable} threadId={threadId} />
                )}
                {event.eventable_type === 'Email' && (
                  <ThreadEmailBox threadId={threadId} event={event.eventable} />
                )}
                {event.eventable_type === 'System Note' && (
                  <ThreadSystemNoteBox event={event.eventable} />
                )}
                {event.eventable_type === 'Incoming Form Submission' && (
                  <ThreadIncomingFormSubmissionBox event={event.eventable} />
                )}
                {event.eventable_type === 'Contact Reply' && (
                  <ThreadContactReplyBox event={event.eventable} />
                )}
              </motion.div>
            ) : null
          )
        : null}
      <div className="flex flex-col">
        {loadingArray.map((_, index) => (
          <div
            key={index}
            className="border-t border-black/[0.06] px-1 py-4 dark:border-white/[0.07]"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="size-8 flex-shrink-0 rounded-full" />
              <Skeleton className="h-3 w-32 rounded" />
            </div>
            <Skeleton
              className="ml-[2.75rem] mt-3 rounded"
              style={{
                height: `${28 + ((index * 15) % 48)}px`,
              }}
            />
          </div>
        ))}
      </div>

      <div ref={messageContentRef} className="hidden" />
    </div>
  )
}

export default ThreadItemBox
